import {type ReactNode, useMemo, useState} from 'react'

import {ModalAction, useToggleModalAction} from '../actions/Modal'
import {useAddDestinationAction} from './AddDestination/useAddDestinationAction'
import {OrderAction, useOrderAction, useOrderActionNdd} from './Order'
import {getTriggerOpenMapActionHandler, RouteMap} from './RouteMap'
import {useSafeFlowAction} from './SafeFlow'
import {getSelectDueAction, SelectDueAction} from './SelectDue'
import {useStateChangeAction} from './state-change'
import {type ActionComponentProps, type ActionConfig, ActionType} from './types'

export const useAction = <T extends ActionConfig>(
  props?: T,
  actionComponentProps?: Pick<ActionComponentProps, 'onChange'>,
): {ActionComponent: ReactNode; triggerAction?: VoidFunction} => {
  const [isActive, setActive] = useState(false)
  const triggerAddDestinationAction = useAddDestinationAction()
  const triggerOrderAction = useOrderAction()
  const triggerOrderActionNdd = useOrderActionNdd(setActive)
  const triggerModalAction = useToggleModalAction(setActive)
  const triggerSafeFlowAction = useSafeFlowAction()
  const triggerStateChange = useStateChangeAction()

  const [ActionWidget, triggerAction] = useMemo<[ReactNode, VoidFunction?]>(() => {
    if (!props) {
      return [null]
    }

    if (!props.type) {
      throw new Error('Action type is not found')
    }

    switch (props.type) {
      case ActionType.ADD_DESTINATION:
        return [null, triggerAddDestinationAction]

      case ActionType.OPEN_MAP:
        return [
          isActive ? (
            <RouteMap
              onClose={() => {
                setActive(false)
              }}
            />
          ) : null,
          getTriggerOpenMapActionHandler(setActive),
        ]

      case ActionType.SAFE_FLOW:
        return [null, triggerSafeFlowAction]

      case ActionType.ORDER:
        return props?.widget_id?.includes('ndd_order_button_widget')
          ? [
              <OrderAction
                key="ndd_onboarding_modal"
                isActive={isActive}
                {...actionComponentProps}
                setActive={setActive}
                {...props}
              />,
              triggerOrderActionNdd,
            ]
          : [null, triggerOrderAction]

      case ActionType.SELECT_DUE:
        return [
          <SelectDueAction
            key="select-due-action"
            {...props}
            {...actionComponentProps}
            isActive={isActive}
            setActive={setActive}
          />,
          getSelectDueAction(setActive),
        ]

      case ActionType.MODAL:
        return [
          isActive && <ModalAction {...props} {...actionComponentProps} isActive={isActive} setActive={setActive} />,
          triggerModalAction,
        ]

      case ActionType.CHANGE_STATE:
        return [
          null,
          () => {
            triggerStateChange(props)
          },
        ]

      default:
        return [null]
    }
  }, [
    props,
    triggerAddDestinationAction,
    isActive,
    actionComponentProps,
    triggerOrderActionNdd,
    triggerOrderAction,
    triggerModalAction,
    triggerSafeFlowAction,
    triggerStateChange,
  ])

  return {
    ActionComponent: ActionWidget,
    triggerAction,
  }
}
